import type { CompressionResult, DocumentFileType } from '../../types';

export interface ProcessedFile {
  file: File;
  result: CompressionResult;
  previewUrl?: string;
}

export interface CompressionOptions {
  targetBytes: number;
  exceptionBytes: number;
  knownHashes?: Iterable<string | undefined>;
  qualityFloor?: number;
  maxDimension?: number;
}

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/tiff']);

export async function hashBlob(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function inferFileType(file: File): DocumentFileType {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const allowed: DocumentFileType[] = [
    'pdf', 'jpg', 'jpeg', 'png', 'webp', 'tiff',
    'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt',
  ];
  return allowed.includes(ext as DocumentFileType) ? ext as DocumentFileType : 'other';
}

async function isDigitallySignedPdf(file: File): Promise<boolean> {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return false;
  const sample = await file.slice(0, Math.min(file.size, 2_000_000)).text();
  return /\/ByteRange\s*\[/.test(sample) || /\/SubFilter\s*\/adbe\.pkcs7/.test(sample);
}

async function getPdfPageCount(file: File): Promise<number> {
  try {
    const { PDFDocument } = await import('pdf-lib');
    const pdf = await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: true });
    return pdf.getPageCount();
  } catch {
    return 1;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('تعذر إنشاء الملف المضغوط')),
      'image/jpeg',
      quality,
    );
  });
}

async function decodeImage(file: File): Promise<{ source: CanvasImageSource; width: number; height: number; close?: () => void }> {
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
  }

  const url = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  return {
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    close: () => URL.revokeObjectURL(url),
  };
}

async function compressImage(file: File, targetBytes: number, qualityFloor: number, maxDimension: number): Promise<File> {
  if (file.size <= targetBytes) return file;

  const decoded = await decodeImage(file);
  try {
    let scale = Math.min(1, maxDimension / Math.max(decoded.width, decoded.height));
    let quality = 0.9;
    let bestBlob: Blob | null = null;

    for (let attempt = 0; attempt < 14; attempt += 1) {
      const width = Math.max(640, Math.round(decoded.width * scale));
      const height = Math.max(640, Math.round(decoded.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('المتصفح لا يدعم معالجة الصور');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(decoded.source, 0, 0, width, height);

      const blob = await canvasToBlob(canvas, quality);
      if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
      if (blob.size <= targetBytes) {
        return new File([blob], replaceExtension(file.name, 'jpg'), {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }

      if (quality > qualityFloor + 0.06) {
        quality = Math.max(qualityFloor, quality - 0.09);
      } else {
        scale *= 0.82;
        quality = Math.min(0.82, Math.max(qualityFloor, quality + 0.04));
      }
    }

    if (!bestBlob) return file;
    return new File([bestBlob], replaceExtension(file.name, 'jpg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } finally {
    decoded.close?.();
  }
}

function replaceExtension(name: string, ext: string): string {
  const stem = name.replace(/\.[^.]+$/, '') || 'document';
  return `${stem}.${ext}`;
}

export async function processFile(file: File, options: CompressionOptions): Promise<ProcessedFile> {
  const qualityFloor = options.qualityFloor ?? 0.48;
  const maxDimension = options.maxDimension ?? 2200;
  const signed = await isDigitallySignedPdf(file);
  const inputHash = await hashBlob(file);
  const isDuplicate = new Set(options.knownHashes ?? []).has(inputHash);
  let output = file;
  let pageCount = 1;
  let status: CompressionResult['status'] = 'skipped';
  let errorMessage: string | undefined;

  try {
    if (IMAGE_TYPES.has(file.type) || /\.(jpe?g|png|webp|tiff?)$/i.test(file.name)) {
      output = await compressImage(file, options.targetBytes, qualityFloor, maxDimension);
      status = output.size < file.size ? 'done' : 'skipped';
    } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      pageCount = await getPdfPageCount(file);
      status = 'skipped';
      if (file.size > options.targetBytes) {
        errorMessage = signed
          ? 'الملف موقع رقمياً، لذلك لم يتم تعديله حفاظاً على صحة التوقيع.'
          : 'ضغط PDF المتقدم يتطلب خدمة خادم؛ تم الاحتفاظ بالنسخة الأصلية دون إتلاف الجودة.';
      }
    }
  } catch (error) {
    status = 'failed';
    errorMessage = error instanceof Error ? error.message : 'فشل ضغط الملف';
    output = file;
  }

  const outputHash = output === file ? inputHash : await hashBlob(output);
  const ratio = file.size > 0 ? output.size / file.size : 1;
  const exceededLimit = output.size > options.targetBytes;
  const exceptionRequired = output.size > options.exceptionBytes;
  const quality = status === 'done' ? Math.max(60, Math.round(96 - (1 - ratio) * 25)) : 100;

  return {
    file: output,
    previewUrl: output.type.startsWith('image/') ? URL.createObjectURL(output) : undefined,
    result: {
      originalBytes: file.size,
      compressedBytes: output.size,
      ratio,
      quality,
      pageCount,
      status,
      errorMessage,
      isDigitallySigned: signed,
      isDuplicate,
      contentHash: outputHash,
      outputMimeType: output.type || file.type || 'application/octet-stream',
      outputFileName: output.name,
      exceededLimit,
      exceptionRequired,
    },
  };
}

export async function imagesToPdf(files: File[], targetBytes: number): Promise<File> {
  if (files.length === 0) throw new Error('لم يتم اختيار صور');
  const { PDFDocument } = await import('pdf-lib');
  const pdf = await PDFDocument.create();
  const perPageTarget = Math.max(120_000, Math.floor(targetBytes / files.length));

  for (const source of files) {
    const processed = await processFile(source, {
      targetBytes: perPageTarget,
      exceptionBytes: Math.max(targetBytes * 4, perPageTarget),
      qualityFloor: 0.5,
      maxDimension: 1800,
    });
    const bytes = await processed.file.arrayBuffer();
    const image = processed.file.type === 'image/png'
      ? await pdf.embedPng(bytes)
      : await pdf.embedJpg(bytes);
    const page = pdf.addPage([image.width, image.height]);
    page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
    if (processed.previewUrl) URL.revokeObjectURL(processed.previewUrl);
  }

  const bytes = await pdf.save({ useObjectStreams: true });
  // Copy into a plain ArrayBuffer so the File constructor never receives a
  // SharedArrayBuffer-backed view under newer TypeScript DOM typings.
  const pdfBuffer = new Uint8Array(bytes.byteLength);
  pdfBuffer.set(bytes);
  return new File([pdfBuffer.buffer], `scanned-document-${Date.now()}.pdf`, {
    type: 'application/pdf',
    lastModified: Date.now(),
  });
}
